import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { useResource } from '../../hooks';
import { EmptyState, LoadingRows, StatusBadge } from '../../components/ui';
import { DeploymentHistory } from '../../components/deploy/DeploymentHistory';
import { ResponsiveTable } from '../../components/primitives/ResponsiveTable';
import type { Column } from '../../components/primitives/ResponsiveTable';
import { Initiator, WhenCell } from '../../components/status/StatusPieces';
import type { ExecutionRun } from '../../api';
import { DeploymentTimelineView } from '../../components/DeploymentTimelineView';
import { DeploymentGroupView } from '../../components/DeploymentGroupView';
import { ExecutionRunView } from '../../components/ExecutionRunView';
import { PanelCard, ProjectSection, QuietEmpty, SectionHeader } from '../../components/project/ui';
import { Callout, Tabs } from '../../components/primitives';
import { labelFor, workflowLabel } from '../../labels';
import { useProject } from './ProjectContext';
import { SSHEnvironmentNotice, isOperatedOverSSH } from '../../components/project/SSHEnvironmentNotice';
import { DatabasePreviewView, DatabasePreviewsPanel } from '../../components/project/DatabasePreviews';

/**
 * DeploymentsSection is the operational screen for releases: deploy, the
 * history of what was deployed, and — one click away — the execution
 * timeline of any one of them.
 *
 * Runs on the server (setup, backups, restores, fixes) are the same
 * machinery but not the same question, so they are a secondary view here
 * rather than a second table competing with the deployment history.
 */
type SectionView = 'deployments' | 'runs' | 'previews';

export function DeploymentsSection() {
  const project = useProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const { environment, sectionPath, deployments } = project;

  const openDeploymentId = searchParams.get('deployment');
  const openGroupId = searchParams.get('group');
  const openRunId = searchParams.get('run');
  const openPreviewId = searchParams.get('preview');
  // Database change previews read a Magento release's files, so a
  // WordPress project has none.
  const magento = project.isWordPress ? undefined : project.applications.find((app) => app.type === 'magento_backend' && !app.disabled);
  const requestedView = searchParams.get('view');
  const view: SectionView = requestedView === 'runs' ? 'runs' : requestedView === 'previews' && magento ? 'previews' : 'deployments';
  // One-shot intents from an incident's suggested actions (UX-01): open the
  // deployment's log, or its rollback dialog.
  const intent = searchParams.get('intent');
  const initialAction = intent === 'rollback' || intent === 'log' ? intent : undefined;

  function clearIntent() {
    if (!searchParams.has('intent')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('intent');
    setSearchParams(next, { replace: true });
  }

  function close() {
    const next = new URLSearchParams(searchParams);
    next.delete('deployment');
    next.delete('group');
    next.delete('run');
    next.delete('preview');
    next.delete('intent');
    setSearchParams(next, { replace: true });
  }

  function setView(nextView: SectionView) {
    const next = new URLSearchParams(searchParams);
    next.delete('deployment');
    next.delete('group');
    next.delete('run');
    next.delete('preview');
    if (nextView === 'deployments') next.delete('view');
    else next.set('view', nextView);
    setSearchParams(next);
  }

  if (!environment) {
    return (
      <ProjectSection>
        <EmptyState
          title="No environment selected"
          description="Add an environment in Settings."
        />
      </ProjectSection>
    );
  }

  if (openDeploymentId) {
    return (
      <ProjectSection>
        <DeploymentTimelineView
          deploymentId={openDeploymentId}
          onRefresh={project.refresh}
          onBackToList={close}
          initialAction={initialAction}
          onInitialActionHandled={clearIntent}
          sectionPath={project.sectionPath}
          environmentName={project.environment?.name}
          site={project.environmentHealth.data?.site}
          repositoryOf={(applicationId) => project.applications.find((app) => app.id === applicationId)?.repository_url || project.project.repository_url}
        />
      </ProjectSection>
    );
  }

  if (openGroupId) {
    return (
      <ProjectSection>
        <DeploymentGroupView
          groupId={openGroupId}
          onOpenDeployment={(id) => setSearchParams(new URLSearchParams({ env: environment.slug, deployment: id }))}
          onRetry={(applicationId) => project.openDeploy([applicationId])}
          onBack={() => {
            project.refresh();
            close();
          }}
        />
      </ProjectSection>
    );
  }

  if (openPreviewId) {
    return (
      <ProjectSection>
        <DatabasePreviewView
          previewId={openPreviewId}
          runPath={(executionId) => sectionPath('deployments', { run: executionId, view: 'runs' })}
          onBack={() => setView('previews')}
        />
      </ProjectSection>
    );
  }

  if (openRunId) {
    return (
      <ProjectSection>
        <ExecutionRunView executionId={openRunId} sectionPath={project.sectionPath} onBack={close} onSettled={project.refresh} />
      </ProjectSection>
    );
  }

  const deploymentList = deployments.data?.deployments ?? [];

  return (
    <ProjectSection>
      {isOperatedOverSSH(environment, project.isWordPress) ? <SSHEnvironmentNotice environment={environment!} what="Deployments" /> : null}
      <PanelCard labelledBy="deployments-title" testId="deployments-panel">
        <SectionHeader
          id="deployments-title"
          title={`Deployments to ${environment.name}`}
          // The header's Deploy is the page's one deploy action (audit B1,
          // CO-150): this panel never repeats it.
          actions={
            deploymentList.length > 0 && !project.placedPrimary ? (
              <Link className="button button--secondary" to={sectionPath(project.isWordPress ? 'site' : 'setup')}>
                {project.isWordPress ? 'Connect the site' : 'Finish server setup'}
              </Link>
            ) : undefined
          }
        />

        <Tabs
          label="Deployment history or server runs"
          variant="secondary"
          idBase="deployments-view-tab"
          panelId="deployments-view"
          value={view}
          onChange={setView}
          options={[
            {
              value: 'deployments',
              label: 'Deployments',
              count: deploymentList.length > 0 ? deploymentList.length : undefined,
            },
            // Runs load only when their view opens, so this tab has no count.
            { value: 'runs', label: 'Runs on the server', testId: 'runs-tab' },
            ...(magento ? [{ value: 'previews' as const, label: 'Database previews', testId: 'previews-tab' }] : []),
          ]}
        />

        <div
          role="tabpanel"
          id="deployments-view"
          aria-labelledby={`deployments-view-tab-${view}`}
          className="view-switch__panel"
        >
        {view === 'deployments' ? (
          deployments.loading && deploymentList.length === 0 ? (
            <LoadingRows label="Loading deployments" rows={5} rowHeight="56px" />
          ) : deployments.error && deploymentList.length === 0 ? (
            <Callout
              tone="warning"
              title="The deployment history could not be read"
              testId="deployments-error"
              actions={
                <button type="button" className="button button--secondary button--small" onClick={deployments.reload}>
                  Try again
                </button>
              }
            >
              {deployments.error.message}
            </Callout>
          ) : deploymentList.length === 0 ? (
            <QuietEmpty testId="no-deployments">
              No deployments
              {!project.placedPrimary ? (
                <>
                  {' · '}
                  <Link to={sectionPath(project.isWordPress ? 'site' : 'setup')}>{project.isWordPress ? 'Connect the site' : 'Continue setup'}</Link>
                </>
              ) : project.isWordPress ? null : (
                <>
                  {' · '}
                  <Link to={sectionPath('recovery')}>Import an existing store first</Link>
                </>
              )}
            </QuietEmpty>
          ) : (
            <DeploymentHistory
              deployments={deploymentList}
              serving={deployments.data?.serving}
              environmentName={environment.name}
              serverName={project.serverName}
              deploymentPath={(id) => sectionPath('deployments', { deployment: id })}
              groupPath={(id) => sectionPath('deployments', { group: id })}
              repositoryOf={(deployment) => project.applications.find((app) => app.id === deployment.application_id)?.repository_url || project.project.repository_url}
            />
          )
        ) : view === 'previews' && magento ? (
          <DatabasePreviewsPanel
            projectId={project.projectId}
            environmentId={environment.id}
            magentoId={magento.id}
            defaultBranch={magento.default_branch ?? ''}
            previewPath={(id) => sectionPath('deployments', { preview: id, view: 'previews' })}
          />
        ) : (
          <ServerRuns />
        )}
        </div>
      </PanelCard>
    </ProjectSection>
  );
}

/**
 * ServerRuns is the raw execution history, kept reachable rather than
 * deleted: every step-by-step run the agent performed for this
 * environment, whatever started it. A run that carried a deployment links
 * to it, and the deployment links back (CO-150).
 */
function ServerRuns() {
  const project = useProject();
  const { environment, sectionPath } = project;
  const runs = useResource(
    () =>
      environment
        ? api.listEnvironmentExecutions(environment.id).catch(() => ({ executions: [] }))
        : Promise.resolve({ executions: [] }),
    [environment?.id],
  );

  const executions = runs.data?.executions ?? [];
  const deploymentOf = (run: ExecutionRun) => (project.deployments.data?.deployments ?? []).find((deployment) => deployment.execution_id === run.id);

  if (runs.loading && executions.length === 0) return <LoadingRows label="Loading runs" rows={4} rowHeight="52px" />;
  if (executions.length === 0) {
    return (
      <QuietEmpty testId="no-runs">No runs</QuietEmpty>
    );
  }

  const columns: Column<ExecutionRun>[] = [
    {
      id: 'what',
      header: 'Run',
      card: 'title',
      width: 20,
      cell: (run) => (
        <span className="cell-stack">
          <span className="cell-stack__primary">{labelFor(workflowLabel, run.workflow_type)}</span>
          <span className="cell-stack__secondary">
            <code translate="no">{run.id.slice(0, 8)}</code>
            {run.mode === 'rehearsal' ? ' · rehearsal, nothing changed' : run.plan_version ? ` · plan v${run.plan_version}` : null}
          </span>
        </span>
      ),
    },
    {
      id: 'components',
      header: 'Components',
      width: 'fill',
      cell: (run) => {
        const deployment = deploymentOf(run);
        return (
          <span className="cell-stack">
            <span>{project.applicationNames(run.application_ids) || <span className="muted">Whole environment</span>}</span>
            {deployment ? (
              <Link className="cell-stack__secondary" to={sectionPath('deployments', { deployment: deployment.id })}>
                Deployment of {deployment.source_commit_sha.slice(0, 7)}
              </Link>
            ) : null}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Outcome',
      card: 'status',
      width: 'status',
      cell: (run) => <StatusBadge kind="execution" value={run.status} />,
    },
    {
      id: 'when',
      header: 'Started',
      width: 'time',
      cell: (run) => <WhenCell created={run.created_at} started={run.started_at} completed={run.completed_at} running={run.status === 'running' || run.status === 'preflighting'} />,
    },
    { id: 'server', header: 'Server', priority: 'secondary', card: 'none', width: 8, cell: (run) => <span className="cell-stack__secondary">{project.serverName(run.server_id)}</span> },
    { id: 'by', header: 'Started by', priority: 'secondary', card: 'none', width: 12, cell: (run) => <Initiator value={run.created_by} /> },
  ];

  return (
    <div className="history">
      <ResponsiveTable
        rows={executions}
        columns={columns}
        rowKey={(run) => run.id}
        caption="Runs on the server, newest first"
        testId="server-runs"
        rowLink={(run) => ({ to: sectionPath('deployments', { run: run.id, view: 'runs' }), accessibleName: `View the log of run ${run.id.slice(0, 8)}` })}
      />
    </div>
  );
}
